/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 10/26/2022
 * for support.userx.co
 *
 * @todo Add bulk actions to TableBlock to allow quick status changes to multiple tickets.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import Badge from "react-bootstrap/Badge";
import {useNavigate} from "react-router";
import {TICKET_PRIORITIES, TICKET_STATUS, TICKET_TYPES} from "../../system/defaults";
import useLocalStorage from "../../_hooks/useLocalStorage";
import {formatDate, shorten} from "../../_utilities/formattingUtils";
import Checkbox from "./formElements/Checkbox";
import SelectorInput from "./formElements/SelectorInput";
import {useUser} from "../../_contexts/UserContext";
import useDefaultsManager from "../../_hooks/useDefaultsManager";
import ticket from "../Ticket";
import supportAPI from "../../_apis/supportAPI";

const TableBlock = ({title, items, classes = null, style = null, showClientList=true}) => {
    const navigate = useNavigate();
    const {userContext} = useUser();
    const statii = useDefaultsManager('status-defaults');
    const types = useDefaultsManager('type-defaults');
    const priorities = useDefaultsManager('priority-defaults');
    const [showMaintenanceOnly, setShowMaintenanceOnly] = useState(true);
    const [agentFilters, setAgentFilters] = useLocalStorage('savedAgentFilters');
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [sortBy, setSortBy] = useLocalStorage('savedSortBy', 'priorityId');
    const [sortDir, setSortDir] = useLocalStorage('savedSortDir', 'DESC');
    const [filters, setFilters] = useLocalStorage('savedFilters', {
        'Need More Info':      false,
        'Cancelled':     false,
        'In Progress':    false,
        'Testing':       false,
        'Backlog':       false,
        'Ready To Launch': false,
        'Assigned':      true,
        'companyFilter': '-1',
    });
    const [assignedOnly, setAssignedOnly] = useLocalStorage('true');

    // Cache: companyId -> boolean (has Maintenance service)
    const maintenanceCompanyCache = useRef(new Map());
    // bump to re-render after cache updates
    const [cacheVersion, setCacheVersion] = useState(0);

    useEffect(() => {
        statii.fetchDefaults();
        types.fetchDefaults();
        priorities.fetchDefaults();
    }, []);

    useEffect(() => {
        if (items.length > 0) {
            
            const companyIds = [...new Set(
                items.filter(i => i.company_id).map(i => i.company_id)
            )];
            const companyNames = [];

            const toFetch = companyIds.filter(id => !maintenanceCompanyCache.current.get(id));
            if(toFetch.length){
                (async () => {
                    await Promise.all(toFetch.map(async (companyId) => {
                        let companyName = 'Unknown';
                        let hasMaintenance = false;
                        try {
                            const res = await supportAPI.get(`/company/${companyId}`);
                            const meta = res?.data?.meta ?? res?.data ?? [];
                            hasMaintenance = hasMaintenanceService(meta);
                            companyName = res.data.name;

                            let companyCache = {
                                hasMaintenance,
                                name: companyName
                            };
                            maintenanceCompanyCache.current.set(companyId, companyCache);
                        } catch(err) {
                            console.error('Failed to fetch company meta', companyId, err);
                            maintenanceCompanyCache.current.set(companyId, {
                                hasMaintenance: false,
                                name: companyName
                            });
                        }
                    }));
                    setCacheVersion(v => v + 1); // re-render to apply new cache values
                })();
            }

            const ticketCompanies = companyIds
                .map(companyId => {
                    const cached = maintenanceCompanyCache.current.get(companyId);
                    const item = items.find(item => item.company_id === companyId);
                    return {
                        id: companyId,
                        name: cached?.name || item?.company_name || 'Unknown',
                    }
                })
                .sort((a, b) => a.name.localeCompare(b.name));

            setFilteredCompanies(ticketCompanies);
        }
    }, [items]);

    const handleFilterChange = (name, active) => {
        const updatedFilters = {...filters, [name]: active};
        setFilters(updatedFilters);
    };
    const handleCompanyFilterChange = (e) => {
        const updatedFilters = {...filters, companyFilter: e.target.value};
        setFilters(updatedFilters);
    }
    const handleSortChange = (e) => {
        setSortBy(e.target.value);
    }

    const rowClickHandler = (ticket) => {
        navigate(`/clients/${ticket.company_id}/tickets/${ticket.ticket_id}`);
    }

    const renderRow = (ticket) => {
        if (ticket.on_hold !== "0") return null;
        const priority = priorities.defaults.find(i => parseInt(i.id) === parseInt(ticket.priority_id));
        const status = statii.defaults.find(i => parseInt(i.id) === parseInt(ticket.status_id));
        const type = TICKET_TYPES.find(i => parseInt(i.id) === parseInt(ticket.type_id));

        const option = (
            <div key={`ticket_${ticket.ticket_id}`} className="d-flex align-items-start py-2 mb-2"
                 style={{borderBottom: "1px solid #efefef"}}
                 onClick={() => rowClickHandler(ticket)}>
                <input type='checkbox' className="mt-1 me-3"/>
                <div className="d-flex flex-column">
                    <div style={{fontSize: ".9em"}}>
                        <Badge className='me-1' bg={priority?.color} title={priority?.name}>{status?.name}</Badge>
                        #{ticket.ticket_id} |&nbsp;
                        {type ? type.name : null} | Submitted: {formatDate(ticket.date_created)} |
                        {ticket.date_due ?
                            <>&nbsp;<strong className="text-dark">Due: {formatDate(ticket.date_due)} | </strong></> : null}
                        {userContext.contact.group_id === "1" && ticket.company_id ?
                            <>&nbsp;<a href={`/clients/${ticket.company_id}`}
                               style={{color: "#666"}}
                               onClick={(e) => e.stopPropagation()} // Prevent event from bubbling to the outer div
                            >{shorten(maintenanceCompanyCache.current.get(ticket.company_id).name ?? 'Client' +
                                ' Dashboard', 30)}</a></>
                            : shorten(maintenanceCompanyCache.current.get(ticket.company_id).name ?? ' Client Dashboard', 30)}
                    </div>
                    <div style={{fontSize: "1.2em"}}>{ticket.title}</div>
                </div>
            </div>
        );

        if (!showClientList || !filters.companyFilter || filters.companyFilter === '-1' || filters.companyFilter === 'All Clients') {
            return option;
        } else if (ticket.company_name === filters.companyFilter) {
            return option;
        }
    }


    const activeStatusIds = useMemo(() => {
        return Object.entries(filters || {})
        .filter(([k, v]) => k !== 'companyFilter' && v === true)
        .map(([label]) => TICKET_STATUS.find(
            s => String(s.name).toLowerCase() === String(label).toLowerCase()
        )?.id)
        .filter(Number.isFinite);
    }, [filters, TICKET_STATUS]);

    // Apply your existing filters (status, companyFilter, etc.)
    const itemsAfterFilters = useMemo(() => {
        return items.filter(filterItems);
    }, [items, filters]);

// Then apply the Maintenance toggle + cached meta
    const displayedItems = useMemo(() => {
        if (!showMaintenanceOnly) return itemsAfterFilters;

        return itemsAfterFilters.filter(
            t => maintenanceCompanyCache.current.get(t.company_id)
        );
    }, [itemsAfterFilters, showMaintenanceOnly, cacheVersion]);

    return (
        <div className={`ticket-list h-100 bg-light rounded p-3 shadow-sm flex-grow-1 d-flex flex-column ${classes}`}
             style={style}>
            {showClientList
                ? <div className={`d-flex justify-content-between align-items-center flex-wrap`}>
                    <h3 className={`me-3`} style={{minWidth: '200px'}}>{title}</h3>
                    <div className={`flex-grow-1`}>
                        <select className={'w-100'}
                                style={{maxWidth: '100%', height: '30px', padding: '0px 10px'}}
                                value={filters.companyFilter ? filters.companyFilter : -1}
                                onChange={handleCompanyFilterChange}>
                            <option value={-1}>All Clients</option>
                            {filteredCompanies.map((company, index) => {
                                return <option key={`company_${index}`}>{company.name}</option>
                            })}
                        </select>
                    </div>
                    <div className={`form-check form-switch ms-3`}>
                        <input className={`form-check-input`}
                               type={'checkbox'}
                               id={'toggle-maintenance-only'}
                               checked={showMaintenanceOnly}
                               onChange={(e) => setShowMaintenanceOnly(e.target.checked)}
                               title={'Maintenance Only'}
                               />
                        <label className={'form-check-label'}
                               htmlFor={'toggle-maintenance-only'}>Maintenance Only</label>
                    </div>
                </div>
                : null}


            <div className={`my-1 pb-3 d-flex flex-wrap align-items-center justify-content-start`}>
                <div>
                    <strong className={`me-3`}>Sort By:</strong>
                    <select className={`flex-grow-1 me-2`} style={{maxWidth: "150px"}} value={sortBy}
                            onChange={(e) => {
                                setSortBy(e.target.value);
                            }}>
                        <option value={`priorityId`}>Priority</option>
                        <option value={`dateCreated`}>Date Created</option>
                        <option value={`dateDue`}>Date Due</option>
                        <option value={`company`}>Company</option>
                    </select>
                    <select className={`me-4`} style={{maxWidth: "100px"}} value={sortDir}
                            onChange={(e) => {
                                setSortDir(e.target.value);
                            }}>
                        <option value={`ASC`}>UP</option>
                        <option value={`DESC`}>DN</option>
                    </select>
                </div>
                <div className={'d-flex flex-wrap'}>
                    <strong className={`me-3`}>Filters:</strong>
                    {statii.defaults.filter(s=> !['Complete','Cancelled'].includes(s.name)).map(s=>(
                        <Checkbox key={`cb-${s.id}`} name={s.name} label={s.name} classes={`mb-0 pb-0 me-3 mh-1`}
                                  checked={filters[s.name]} strikethrough={false}
                                  changeHandler={active => handleFilterChange(s.name, active)}/>
                    ))}
                </div>
            </div>
            <div className="d-flex flex-column">
                {displayedItems && displayedItems.length > 0
                    ? displayedItems.sort(sortItems).map(item => renderRow(item))
                    : 'Take a break!'}
            </div>
        </div>
    );


    function filterItems(item) {
        if (activeStatusIds.length === 0) return true;
        const statusId = parseInt(item.status_id, 10);
        return activeStatusIds.includes(statusId);
    }

    function sortItems(a, b) {
        const multiplier = sortDir === 'DESC' ? -1 : 1;

        switch (sortBy) {
            case 'priorityId':
                return (parseInt(a.priority_id, 10) - parseInt(b.priority_id, 10)) * multiplier;
            case 'dateCreated':
                return compareDates(a.date_created, b.date_created) * multiplier;
            case 'dateDue':
                return compareDates(a.date_due, b.date_due) * multiplier;
            case 'company':
                return (parseInt(a.company_id, 10) - parseInt(b.company_id, 10)) * multiplier;
            default:
                return 0;
        }
    }

    function compareDates(date1, date2) {
        // Parse the dates and compare them
        const d1 = new Date(date1);
        const d2 = new Date(date2);
        return d1 - d2; // For ascending order. Flip `d1` and `d2` for descending.
    }
    function hasMaintenanceService(meta) {
        if (!Array.isArray(meta)) return false;

        // CSV under 'services' / 'service_ids'
        const servicesRow = meta.find(m => ['services', 'service_ids'].includes(m.meta_key));
        if (servicesRow?.meta_value) {
            const ids = String(servicesRow.meta_value)
            .split(/[,\s]+/)
            .map(x => parseInt(x, 10))
            .filter(Number.isFinite);
            return ids.includes(2);
        }

        // Multiple rows like 'service_id' or 'service_*'
        const anyServiceRows = meta
        .filter(m => /^service(_id)?$/i.test(m.meta_key) || /^service_/.test(m.meta_key))
        .some(m => parseInt(m.meta_value, 10) === 2);

        return anyServiceRows;
    }
}

export default TableBlock;